/**
 * Color palette auto-generation utility for SaaS branding.
 *
 * Takes a single hex color and produces a 6-shade palette via HSL manipulation.
 * All returned hex values are lowercase `#rrggbb`.
 *
 * This module is a pure utility with zero dependencies on the rest of the codebase.
 */

/** A 6-shade palette keyed by conventional shade numbers. */
export type ColorPalette = {
  50: string // Very light tint  — HSL(h, s, 95%)
  150: string // Light tint       — HSL(h, s, 82%)
  300: string // Medium light     — HSL(h, s, 65%)
  500: string // Base color       — original
  700: string // Darkened          — HSL(h, s, l * 0.6)
  900: string // Very dark         — HSL(h, s, l * 0.2)
}

/**
 * Normalize and validate a hex color string.
 *
 * Accepts `#RRGGBB` or `RRGGBB` (case-insensitive). Returns the canonical
 * lowercase `#rrggbb` form, or throws on invalid input.
 */
function normalizeHex(hex: string): string {
  let cleaned = hex.startsWith("#") ? hex.slice(1) : hex
  cleaned = cleaned.toLowerCase()

  if (cleaned.length !== 6) {
    throw new Error(`Invalid hex color "${hex}": expected 6 hex digits (with optional leading #).`)
  }

  if (!/^[0-9a-f]{6}$/.test(cleaned)) {
    throw new Error(`Invalid hex color "${hex}": contains non-hex characters.`)
  }

  return `#${cleaned}`
}

/**
 * Convert a hex color (`#rrggbb`) to its HSL representation.
 *
 * @param hex - A hex color string, e.g. `#2563eb` or `2563eb`.
 * @returns An object with `h` (0-360), `s` (0-100), and `l` (0-100).
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const normalized = normalizeHex(hex)

  const r = parseInt(normalized.slice(1, 3), 16) / 255
  const g = parseInt(normalized.slice(3, 5), 16) / 255
  const b = parseInt(normalized.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  // Lightness
  const l = (max + min) / 2

  // Achromatic
  if (delta === 0) {
    return { h: 0, s: 0, l: Math.round(l * 100) }
  }

  // Saturation
  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min)

  // Hue
  let h: number
  if (max === r) {
    h = ((g - b) / delta + (g < b ? 6 : 0)) / 6
  } else if (max === g) {
    h = ((b - r) / delta + 2) / 6
  } else {
    h = ((r - g) / delta + 4) / 6
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

/**
 * Convert HSL values to a hex color string.
 *
 * @param h - Hue in degrees (0-360).
 * @param s - Saturation as a percentage (0-100).
 * @param l - Lightness as a percentage (0-100).
 * @returns A lowercase hex string in `#rrggbb` format.
 */
export function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100
  const lNorm = l / 100

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = lNorm - c / 2

  let r: number
  let g: number
  let b: number

  if (h < 60) {
    ;[r, g, b] = [c, x, 0]
  } else if (h < 120) {
    ;[r, g, b] = [x, c, 0]
  } else if (h < 180) {
    ;[r, g, b] = [0, c, x]
  } else if (h < 240) {
    ;[r, g, b] = [0, x, c]
  } else if (h < 300) {
    ;[r, g, b] = [x, 0, c]
  } else {
    ;[r, g, b] = [c, 0, x]
  }

  const toHex = (v: number): string => {
    const byte = Math.round((v + m) * 255)
    const clamped = Math.max(0, Math.min(255, byte))
    return clamped.toString(16).padStart(2, "0")
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Calculate the relative luminance of a hex color per WCAG 2.x.
 *
 * @see https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function relativeLuminance(hex: string): number {
  const normalized = normalizeHex(hex)

  const rRaw = parseInt(normalized.slice(1, 3), 16) / 255
  const gRaw = parseInt(normalized.slice(3, 5), 16) / 255
  const bRaw = parseInt(normalized.slice(5, 7), 16) / 255

  const linearize = (v: number): number =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)

  return 0.2126 * linearize(rRaw) + 0.7152 * linearize(gRaw) + 0.0722 * linearize(bRaw)
}

/**
 * Compute the WCAG 2.x contrast ratio between two hex colors.
 *
 * @param hex1 - First color in hex format.
 * @param hex2 - Second color in hex format.
 * @returns The contrast ratio (1 to 21).
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const l1 = relativeLuminance(hex1)
  const l2 = relativeLuminance(hex2)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Check whether a foreground/background pair meets WCAG AA contrast (4.5:1
 * for normal text).
 *
 * @param foreground - Foreground (text) color in hex format.
 * @param background - Background color in hex format.
 * @returns `true` if the pair meets WCAG AA for normal text.
 */
export function meetsWcagAA(foreground: string, background: string): boolean {
  return getContrastRatio(foreground, background) >= 4.5
}

/** Minimum lightness floor for shade 900 to prevent pure black. */
const MIN_LIGHTNESS_900 = 5

/**
 * Generate a 6-shade color palette from a single hex base color.
 *
 * The algorithm produces lighter tints (50, 150, 300) by setting fixed
 * lightness values and darker shades (700, 900) by scaling the original
 * lightness downward. Shade 500 is always the exact input color.
 *
 * Edge cases handled:
 * - Very light inputs: lighter shades still get a minimum saturation bump so
 *   they remain visually distinct from pure white.
 * - Very dark inputs: shade 900 is clamped to a minimum lightness of 5%.
 * - Achromatic (gray) inputs: saturation stays at 0.
 *
 * @param hexColor - A hex color string, e.g. `#2563eb`.
 * @returns A {@link ColorPalette} with shades 50 through 900.
 */
export function generatePalette(hexColor: string): ColorPalette {
  const normalized = normalizeHex(hexColor)
  const { h, s, l } = hexToHsl(normalized)

  // For the lighter tints, preserve hue and saturation. When the base color
  // is very light the lighter shades can become indistinguishable from white,
  // so we ensure a minimum saturation (unless the color is achromatic).
  const effectiveSaturation = s === 0 ? 0 : Math.max(s, 10)

  const shade50 = hslToHex(h, effectiveSaturation, 95)
  const shade150 = hslToHex(h, effectiveSaturation, 82)
  const shade300 = hslToHex(h, effectiveSaturation, 65)

  // Shade 500 is the original color exactly as provided (after normalization).
  const shade500 = normalized

  // Darker shades multiply the original lightness.
  const shade700L = Math.round(l * 0.6)
  const shade900L = Math.max(MIN_LIGHTNESS_900, Math.round(l * 0.2))

  const shade700 = hslToHex(h, s, shade700L)
  const shade900 = hslToHex(h, s, shade900L)

  return {
    50: shade50,
    150: shade150,
    300: shade300,
    500: shade500,
    700: shade700,
    900: shade900,
  }
}
